<template lang="pug">
FileUploadUI(
  ref="fileUploadRef"
  :multiple="props.multiple"
  :http-request="localUpload"
  :drag="true"
  @upload-success="handleUploadSuccess"
  @upload-error="handleUploadError")
</template>

<script lang="ts" setup>
// Pug binds this runtime import; ESLint only sees its script-side type use.
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import FileUploadUI from '@/components/ui/file-upload'

type UploadRequest = { file: File }
type UploadResult = { error: unknown | null; data: unknown | null; file: File }

const props = withDefaults(
  defineProps<{
    multiple?: boolean
    upload?: (request: UploadRequest) => unknown
    afterUpload?: (result: UploadResult) => void
  }>(),
  { multiple: false, upload: undefined, afterUpload: undefined },
)

const fileUploadRef = ref<InstanceType<typeof FileUploadUI>>()

async function localUpload(args: UploadRequest) {
  return props.upload ? props.upload(args) : uploadImage(args.file)
}

function handleUploadSuccess(response: unknown, file: File) {
  props.afterUpload?.({ error: null, data: response, file })
}

function handleUploadError(error: unknown, file: File) {
  props.afterUpload?.({ error, data: null, file })
}

async function uploadImage(file?: File): Promise<string | null> {
  if (!file) return null

  const formSec = await useTicket.getSign({
    name: file.name,
    type: file.type,
    size: file.size,
    lastModified: file.lastModified,
    upload_url: window.location.href,
  })
  const fd = new FormData()
  fd.append('OSSAccessKeyId', formSec.OSSAccessKeyId)
  fd.append('policy', formSec.policy)
  fd.append('signature', formSec.signature)
  fd.append('key', formSec.startsWith + formSec.saveName)
  fd.append('success_action_status', '200')
  if (formSec.callback) fd.append('callback', formSec.callback)
  if (formSec.x_upload_url) fd.append('x:upload_url', formSec.x_upload_url)
  fd.append('file', file, formSec.saveName)

  await $fetch(formSec.host, {
    method: 'POST',
    body: fd,
    responseType: 'text',
    retry: 0,
    timeout: 5 * 60 * 1000,
  })
  return formSec.host + '/' + formSec.startsWith + formSec.saveName
}

function selectFile() {
  fileUploadRef.value?.selectFile()
}

defineExpose({
  selectFile,
  upload: localUpload,
  click: selectFile,
  uploadImage,
})
</script>
